import React from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputBase,
  List,
  Paper,
  Popper,
} from "@mui/material";
import SearchResultRow from "global/components/SearchResultRow/SearchResultRow";
import strings from "global/constants/StringConstants";
import { FolderItem } from "../interface";
import templatesStyles from "../Templates.styles";
import templatesTokens from "../Templates.tokens";
import { SearchGlyph } from "./TemplatesIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const styles = templatesStyles;
const { toolbar } = templatesTokens;

interface TemplatesToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  searchAnchorRef: React.MutableRefObject<HTMLDivElement | null>;
  searchOpen: boolean;
  setSearchOpen: (value: boolean) => void;
  isSearching: boolean;
  searchResults: FolderItem[];
  resolveAncestorCrumbs: (item: FolderItem) => string[];
  onSearchSelect: (item: FolderItem) => void;
  disabled: boolean;
  /** Optional trailing controls per result row (the "more options" menu). */
  renderItemActions?: (item: FolderItem) => React.ReactNode;
}

/** Search field above the grid, drawn straight from the screen tokens. */
const TemplatesToolbar = ({
  searchText,
  onSearchChange,
  searchAnchorRef,
  searchOpen,
  setSearchOpen,
  isSearching,
  searchResults,
  resolveAncestorCrumbs,
  onSearchSelect,
  disabled,
  renderItemActions,
}: TemplatesToolbarProps) => (
  <Box sx={styles.toolbar}>
    <Box ref={searchAnchorRef} sx={styles.searchField}>
      <SearchGlyph
        sx={{
          width: toolbar.searchIconSize,
          height: toolbar.searchIconSize,
          color: toolbar.searchIconColor,
          flexShrink: 0,
        }}
      />
      <InputBase
        id="templates_search_box"
        placeholder="Search templates and folders"
        value={searchText}
        onChange={(event) => onSearchChange(event.target.value)}
        disabled={disabled}
        inputProps={{ "aria-label": "Search templates and folders" }}
        sx={styles.searchInput}
      />
      {isSearching && <CircularProgress size={16} aria-label="Searching" />}
    </Box>

    {/* Screen-reader-only announcement of the search state / result count. */}
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "absolute",
        width: "1px",
        height: "1px",
        p: 0,
        m: "-1px",
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    >
      {isSearching
        ? "Searching"
        : searchOpen
          ? searchResults.length
            ? `${searchResults.length} result${
                searchResults.length === 1 ? "" : "s"
              } found`
            : "No matches found"
          : ""}
    </Box>

    <Popper
      open={searchOpen && !!searchAnchorRef.current}
      anchorEl={searchAnchorRef.current}
      placement="bottom-start"
      style={{
        zIndex: 1300,
        width: searchAnchorRef.current?.clientWidth,
      }}
    >
      <ClickAwayListener
        onClickAway={(event) => {
          // A click inside a floating menu / popover (a result row's "more
          // options" menu is portaled to the body, outside this dropdown) is
          // not an outside click — ignore it so opening a row's menu does not
          // collapse the search dropdown out from under it.
          const target = event.target as HTMLElement | null;
          if (target?.closest?.(".MuiPopover-root")) return;
          setSearchOpen(false);
        }}
      >
        <Paper
          elevation={4}
          sx={{
            mt: 0.5,
            maxHeight: 360,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {searchResults.length === 0 ? (
            <EmptyState size="compact" title="No matches" />
          ) : (
            <List dense disablePadding aria-label="Search results">
              {searchResults.map((item) => {
                const isFolder = item.type === strings.folder;
                return (
                  <SearchResultRow
                    key={
                      isFolder
                        ? `f-${item.autoGeneratedId}`
                        : `t-${item.id ?? item.autoGeneratedId}`
                    }
                    isFolder={isFolder}
                    title={item.templateName ?? ""}
                    crumbs={resolveAncestorCrumbs(item)}
                    createdBy={item.requestedBy?.name}
                    createdOn={item.createdOn ?? item.createdAt}
                    onClick={() => onSearchSelect(item)}
                    actions={renderItemActions?.(item)}
                  />
                );
              })}
            </List>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  </Box>
);

export default TemplatesToolbar;
